import { Link } from "react-router-dom";
import useScrollReveal from "@/hooks/useScrollReveal";

interface Service {
  title: string;
  description: string;
  icon: string;
  href: string;
  image: string;
}

interface ServicesOverviewProps {
  tagline: string;
  title: string;
  subtitle: string;
  services: Service[];
}

export default function ServicesOverview({ tagline, title, subtitle, services }: ServicesOverviewProps) {
  const headRef = useScrollReveal<HTMLDivElement>();

  return (
    <section className="bg-white overflow-hidden">
      {/* Minimal centered header — breathing room only */}
      <div ref={headRef} className="sr-hidden text-center px-6 pt-28 pb-20">
        <div className="flex items-center justify-center gap-4 mb-6">
          <span className="gold-rule" />
          <p className="text-gold font-sans text-[11px] font-bold uppercase tracking-[0.22em]">{tagline}</p>
          <span className="gold-rule" />
        </div>
        <h2 className="font-display font-bold text-charcoal mb-5 leading-[0.92]"
            style={{ fontSize: "clamp(40px, 6vw, 80px)" }}>
          {title}
        </h2>
        <p className="text-stone-400 font-sans text-base max-w-md mx-auto leading-relaxed">{subtitle}</p>
      </div>

      {/* ── SERVICE 1 — Full bleed 75vh, text bottom-left ─────────────── */}
      {services[0] && (
        <Link
          to={services[0].href}
          className="group relative block w-full overflow-hidden cursor-pointer"
          style={{ height: "75vh", minHeight: "520px" }}
          aria-label={services[0].title}
        >
          <img
            src={services[0].image}
            alt={services[0].title + " – Glen Davis Photography Bremerton WA"}
            title={services[0].title}
            className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-[1200ms] group-hover:scale-[1.04]"
          />
          {/* Cinematic overlays */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/93 via-black/30 to-black/10" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-transparent to-transparent" />

          {/* Watermark number */}
          <div
            className="absolute top-6 right-8 md:right-14 font-display text-white/[0.06] font-bold select-none pointer-events-none"
            style={{ fontSize: "clamp(80px, 14vw, 180px)", lineHeight: 1 }}
            aria-hidden="true"
          >01</div>

          {/* Content — bottom-left */}
          <div className="absolute bottom-0 left-0 p-10 md:p-16 max-w-2xl">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-6 h-6 flex items-center justify-center text-gold">
                <i className={`${services[0].icon} text-base`}></i>
              </div>
              <span className="text-gold font-sans text-[10px] font-bold uppercase tracking-[0.24em]">01 · Featured</span>
            </div>
            <h3
              className="font-display text-white font-bold mb-4 leading-[0.93]"
              style={{ fontSize: "clamp(28px, 5vw, 68px)" }}
            >
              {services[0].title}
            </h3>
            <p className="text-stone-300 font-sans text-sm leading-relaxed mb-8 max-w-sm hidden md:block">
              {services[0].description}
            </p>
            <span className="inline-flex items-center gap-3 text-gold font-sans text-[11px] font-bold uppercase tracking-widest group-hover:gap-5 transition-all duration-300">
              Explore Service
              <i className="ri-arrow-right-line"></i>
            </span>
          </div>

          {/* Bottom gold sweep */}
          <div className="absolute bottom-0 left-0 w-0 h-[2px] bg-gradient-to-r from-gold to-gold/20 group-hover:w-full transition-all duration-700 ease-out" />
        </Link>
      )}

      {/* ── SERVICES 2 & 3 — 50/50 split, 65vh ──────────────────────── */}
      <div className="flex flex-col md:flex-row">
        {services.slice(1, 3).map((service, i) => (
          <Link
            key={i}
            to={service.href}
            className="group relative overflow-hidden cursor-pointer flex-1"
            style={{ height: "65vh", minHeight: "400px" }}
            aria-label={service.title}
          >
            <img
              src={service.image}
              alt={service.title + " – Glen Davis Photography"}
              title={service.title}
              className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-[1200ms] group-hover:scale-[1.06]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/93 via-black/20 to-transparent" />

            {/* Subtle vertical divider between panels */}
            {i === 0 && (
              <div className="absolute top-0 right-0 w-px h-full bg-white/[0.06] hidden md:block" />
            )}

            {/* Watermark */}
            <div
              className="absolute top-6 right-6 font-display text-white/[0.06] font-bold select-none pointer-events-none"
              style={{ fontSize: "80px", lineHeight: 1 }}
              aria-hidden="true"
            >0{i + 2}</div>

            {/* Content — bottom */}
            <div className="absolute bottom-0 left-0 right-0 p-8 md:p-10">
              <span className="text-gold font-sans text-[10px] font-bold uppercase tracking-[0.22em] block mb-3">
                0{i + 2}
              </span>
              <h3 className="font-display text-white text-2xl md:text-3xl font-bold mb-3 leading-tight">
                {service.title}
              </h3>
              <p className="text-stone-300 font-sans text-xs leading-relaxed max-h-0 overflow-hidden opacity-0 group-hover:max-h-24 group-hover:opacity-100 transition-all duration-500">
                {service.description}
              </p>
              <div className="mt-4 w-0 h-px bg-gold group-hover:w-10 transition-all duration-500" />
            </div>

            <div className="absolute bottom-0 left-0 w-0 h-[2px] bg-gold group-hover:w-full transition-all duration-700" />
          </Link>
        ))}
      </div>

      {/* ── SERVICE 4 — Full bleed reversed, text bottom-right ───────── */}
      {services[3] && (
        <Link
          to={services[3].href}
          className="group relative block w-full overflow-hidden cursor-pointer"
          style={{ height: "60vh", minHeight: "400px" }}
          aria-label={services[3].title}
        >
          <img
            src={services[3].image}
            alt={services[3].title + " – Glen Davis Photography"}
            title={services[3].title}
            className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-[1200ms] group-hover:scale-[1.04]"
          />
          <div className="absolute inset-0 bg-gradient-to-l from-black/90 via-black/40 to-black/10" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />

          {/* Watermark — left side */}
          <div
            className="absolute top-6 left-8 md:left-14 font-display text-white/[0.06] font-bold select-none pointer-events-none"
            style={{ fontSize: "clamp(80px, 14vw, 180px)", lineHeight: 1 }}
            aria-hidden="true"
          >04</div>

          {/* Content — bottom RIGHT, text-right */}
          <div className="absolute bottom-0 right-0 p-10 md:p-16 max-w-2xl text-right">
            <div className="flex items-center justify-end gap-3 mb-5">
              <span className="text-gold font-sans text-[10px] font-bold uppercase tracking-[0.24em]">04 · Commercial</span>
              <div className="w-6 h-6 flex items-center justify-center text-gold">
                <i className={`${services[3].icon} text-base`}></i>
              </div>
            </div>
            <h3
              className="font-display text-white font-bold mb-4 leading-[0.93]"
              style={{ fontSize: "clamp(28px, 4.5vw, 60px)" }}
            >
              {services[3].title}
            </h3>
            <p className="text-stone-300 font-sans text-sm leading-relaxed mb-8 max-w-sm ml-auto hidden md:block">
              {services[3].description}
            </p>
            <span className="inline-flex items-center justify-end gap-3 text-gold font-sans text-[11px] font-bold uppercase tracking-widest group-hover:gap-5 transition-all duration-300">
              Explore Service
              <i className="ri-arrow-right-line"></i>
            </span>
          </div>

          <div className="absolute bottom-0 right-0 w-0 h-[2px] bg-gradient-to-l from-gold to-gold/20 group-hover:w-full transition-all duration-700 ease-out" />
        </Link>
      )}

      {/* ── CTA — minimal, centered, light ───────────────────────────── */}
      <div className="text-center py-20 px-6">
        <Link
          to="/services"
          className="group inline-flex items-center gap-3 px-10 py-4 border border-stone-200 text-charcoal font-sans font-bold text-[11px] uppercase tracking-[0.2em] hover:bg-charcoal hover:text-white hover:border-charcoal transition-all duration-300 whitespace-nowrap cursor-pointer"
        >
          View All Services
          <span className="w-4 h-4 flex items-center justify-center group-hover:translate-x-0.5 transition-transform">
            <i className="ri-arrow-right-line"></i>
          </span>
        </Link>
      </div>
    </section>
  );
}
